import { useMemo } from 'react';

import AutocompleteInput from './AutocompleteInput';
import { useDataSources } from './registry';
import { DataSourceType, SuggestionOption } from './types';

interface DataControllerInputProps {
  // Required controlled props
  value: string;
  onChange: (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

  // Data source configuration
  dataSources: DataSourceType[];

  // Optional autocomplete props
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  multiline?: boolean;
  maxLength?: number;
  minSearchLength?: number;
  maxSuggestions?: number;
  showOnFocus?: boolean;
  enableSpecialCharacters?: boolean;
  valueExtractor?: (suggestion: SuggestionOption) => string;

  // Optional callbacks
  onSuggestionSelect?: (suggestion: SuggestionOption) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

/**
 * DataControllerInput - Enhanced wrapper combining AutocompleteInput with data source management
 *
 * Improvements:
 * - Better error handling and loading states
 * - Memoized suggestions for performance
 * - More robust data source integration
 * - Proper separation of concerns
 * - Special character query handling at data source level
 */
const DataControllerInput: React.FC<DataControllerInputProps> = ({
  dataSources,
  value,
  ...autocompleteProps
}) => {
  // Get suggestions from configured data sources
  // Pass the current value so data sources can handle special character queries
  const suggestions = useDataSources(dataSources, value);

  // Memoize suggestions to prevent unnecessary re-renders
  const memoizedSuggestions = useMemo(() => {
    try {
      return suggestions || [];
    } catch (error) {
      // In production, log errors without console.error to avoid spam
      if (process.env.NODE_ENV === 'development') {
        console.error('Error processing suggestions:', error);
      }
      return [];
    }
  }, [suggestions]);

  return (
    <AutocompleteInput {...autocompleteProps} value={value} suggestions={memoizedSuggestions} />
  );
};

export default DataControllerInput;
